import { Button, Col, Result, Row } from 'antd';
import React, { useContext, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import paymentApiV2 from '../../apis/v2/PaymentApiV2';
import { CheckoutSessionCreationV2 } from '../../model/v2/EntitiesV2';
import notificationService from '../../services/NotificationService';
import styles from './SubscriptionPage.module.scss';
import { SubscriptionPackage, SubscriptionPeriod } from '../../model/v2/TypesV2';
import CustomContext from '../../context/CustomContext';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';

const SubscriptionPage: React.FC = () => {
    /*** HOOKS ***/

    const intl = useIntl();
    const context = useContext(CustomContext);
    const { auth } = context;
    const isCollegeCoach = auth && auth.authorities.includes('ROLE_COLLEGE_COACH');
    const isPlayer = auth && auth.authorities.includes('ROLE_PLAYER');
    const screens = useBreakpoint();
    const desktop: boolean = Object.entries(screens)
        .filter((screen) => !!screen[1])
        .some((s) => ['md', 'lg', 'xl', 'xxl'].includes(s[0]));

    const [loading, setLoading] = useState<
        | 'loadingMonthlyPerformanceSubscription'
        | 'loadingYearlyPerformanceSubscription'
        | 'loadingMonthlyRecruitmentSubscription'
        | 'loadingYearlyRecruitmentSubscription'
    >();

    /** METHODS **/

    const redirectToStripePayment = async (
        subscriptionPackage: SubscriptionPackage,
        subscriptionPeriod: SubscriptionPeriod,
        loading:
            | 'loadingMonthlyPerformanceSubscription'
            | 'loadingYearlyPerformanceSubscription'
            | 'loadingMonthlyRecruitmentSubscription'
            | 'loadingYearlyRecruitmentSubscription',
    ) => {
        try {
            setLoading(loading);

            const origin = window.location.origin;
            const checkoutSessionCreationV2: CheckoutSessionCreationV2 = {
                successUrl: `${origin}/subscription/success?session_id={CHECKOUT_SESSION_ID}`,
                cancelUrl: `${origin}/subscription`,
                subscriptionPackage,
                subscriptionPeriod,
            };
            const checkoutSession = await paymentApiV2.createCheckoutSession(checkoutSessionCreationV2);
            window.location.href = checkoutSession.url;
        } catch (error: any) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    /*** COMPONENTS ***/

    if (isCollegeCoach) {
        return (
            <Result
                icon={<></>}
                title={<FormattedMessage id="subscription.title" tagName="h1" />}
                subTitle={
                    <p className={styles.description}>
                        <FormattedMessage id="subscription.description" />
                    </p>
                }
                extra={
                    <>
                        <Row gutter={[28, 0]}>
                            <Col xs={24}>
                                <p className={styles.subtitle}>
                                    <FormattedMessage id="subscription.recruitment.title" />
                                </p>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24}>
                                <p>
                                    <FormattedMessage id="subscription.recruitment.description.collegeCoach" />
                                </p>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={8}>
                                <Button
                                    type="primary"
                                    size="large"
                                    className={styles.subscribe}
                                    onClick={() =>
                                        redirectToStripePayment(
                                            'RECRUITMENT',
                                            'MONTHLY',
                                            'loadingMonthlyRecruitmentSubscription',
                                        )
                                    }
                                    loading={loading === 'loadingMonthlyRecruitmentSubscription'}
                                >
                                    <FormattedMessage id="subscription.recruitment.subscribe.monthly" tagName="span" />
                                </Button>
                            </Col>
                            <Col xs={24} md={8}>
                                <Button
                                    type="primary"
                                    size="large"
                                    className={styles.subscribe}
                                    onClick={() =>
                                        redirectToStripePayment(
                                            'RECRUITMENT',
                                            'YEARLY',
                                            'loadingYearlyRecruitmentSubscription',
                                        )
                                    }
                                    loading={loading === 'loadingYearlyRecruitmentSubscription'}
                                >
                                    <FormattedMessage id="subscription.recruitment.subscribe.yearly" tagName="span" />
                                </Button>
                            </Col>
                            <Col xs={24} md={8}>
                                <Link to="/">
                                    <Button type="default" size="large">
                                        <FormattedMessage id="subscription.back" tagName="span" />
                                    </Button>
                                </Link>
                            </Col>
                        </Row>
                    </>
                }
                className={styles.result}
            />
        );
    } else {
        return (
            <Result
                icon={<></>}
                title={<FormattedMessage id="subscription.title" tagName="h1" />}
                subTitle={
                    <p className={styles.description}>
                        <FormattedMessage id="subscription.description" />
                    </p>
                }
                extra={
                    <>
                        <Row gutter={[28, 0]}>
                            <Col xs={24}>
                                <p className={styles.subtitle}>
                                    <FormattedMessage id="subscription.performance.title" />
                                </p>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24}>
                                <p>
                                    <FormattedMessage id="subscription.performance.description" />
                                </p>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24} md={8}>
                                <Button
                                    type="primary"
                                    size="large"
                                    className={styles.subscribe}
                                    onClick={() =>
                                        redirectToStripePayment(
                                            'PERFORMANCE',
                                            'MONTHLY',
                                            'loadingMonthlyPerformanceSubscription',
                                        )
                                    }
                                    loading={loading === 'loadingMonthlyPerformanceSubscription'}
                                >
                                    <FormattedMessage id="subscription.performance.subscribe.monthly" tagName="span" />
                                </Button>
                            </Col>
                            <Col xs={24} md={8}>
                                <Button
                                    type="primary"
                                    size="large"
                                    className={styles.subscribe}
                                    onClick={() =>
                                        redirectToStripePayment(
                                            'PERFORMANCE',
                                            'YEARLY',
                                            'loadingYearlyPerformanceSubscription',
                                        )
                                    }
                                    loading={loading === 'loadingYearlyPerformanceSubscription'}
                                >
                                    <FormattedMessage id="subscription.performance.subscribe.yearly" tagName="span" />
                                </Button>
                            </Col>
                            <Col xs={24} md={8}>
                                <Link to={isPlayer ? '/events' : '/'}>
                                    <Button type="default" size="large">
                                        <FormattedMessage id="subscription.back" tagName="span" />
                                    </Button>
                                </Link>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24}>
                                <p className={styles.subtitle}>
                                    <FormattedMessage id="subscription.recruitment.title" />
                                </p>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]}>
                            <Col xs={24}>
                                <p>
                                    <FormattedMessage id="subscription.recruitment.description" />
                                </p>
                            </Col>
                        </Row>
                        <Row gutter={[28, 0]} className={styles.recruitment}>
                            <Col xs={24} md={12} className={desktop ? styles.buttonLeft : ''}>
                                <Button
                                    type="primary"
                                    size="large"
                                    className={styles.subscribe}
                                    onClick={() =>
                                        redirectToStripePayment(
                                            'RECRUITMENT',
                                            'MONTHLY',
                                            'loadingMonthlyRecruitmentSubscription',
                                        )
                                    }
                                    loading={loading === 'loadingMonthlyRecruitmentSubscription'}
                                >
                                    <FormattedMessage id="subscription.recruitment.subscribe.monthly" tagName="span" />
                                </Button>
                            </Col>
                            <Col xs={24} md={12} className={desktop ? styles.buttonRight : ''}>
                                <Button
                                    type="primary"
                                    size="large"
                                    className={styles.subscribe}
                                    onClick={() =>
                                        redirectToStripePayment(
                                            'RECRUITMENT',
                                            'YEARLY',
                                            'loadingYearlyRecruitmentSubscription',
                                        )
                                    }
                                    loading={loading === 'loadingYearlyRecruitmentSubscription'}
                                >
                                    <FormattedMessage id="subscription.recruitment.subscribe.yearly" tagName="span" />
                                </Button>
                            </Col>
                        </Row>
                    </>
                }
                className={styles.result}
            />
        );
    }
};
export default SubscriptionPage;
